/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : TicketsTable.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import React, { useEffect, useState, useRef } from "react";
import { TICKETS_TYPES } from "../../../config/appConstants";
import {
  Box,
  Button,
  Chip,
  FormControl,
  IconButton,
  Menu,
  MenuItem,
  Select,
  styled,
  Tooltip,
  Typography,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import softwareIntersectionAPI from "../../../redux/actions/softwareIntersectionActions";
import {
  HandleApiActions,
  labCar,
  profileActions,
  startLoading,
  stopLoading,
  userManagementApis,
} from "../../../redux/actions";
import { componentStyle } from "../../../config/appTheme";
import CustomDialogBoxV2 from "../../../components/customComponentsV2/common/customDialogBox/CustomDialogBoxV2";
import CustomButtonV2 from "../../../components/customComponentsV2/common/button/CustomButtonV2";
import { ConvertUTCToDate } from "../../../utils/convertUtcToTimeZone";
import { useNavigate } from "react-router-dom";
import ModuleViewMoreDialog from "../../../components/customComponentsV2/dashboard/pages/tickets/ModuleViewMoreDialog";
import CustomDataGridV2 from "../../../components/customComponentsV2/common/datagrid/CustomDataGridV2";
import CustomCheckBoxV2 from "../../../components/customComponentsV2/common/checkBox/CustomCheckBoxV2";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { toast } from "react-toastify";
import TicketAttachment from "./TicketAttachment";
import typeApprovalAPI from "../../../redux/actions/typeApprovalAPI";
import CustomToolTip from "../../../components/infoButton/CustomToolTip";

function TicketsTable({
  tickets_type = TICKETS_TYPES.EE,
  ticketDetails,
  hasTicketNeedMore,
  setSelectedTicketType,
  fetchTickets,
  selectedIntersectionIds,
  setSelectedIntersectionIds,
  enableSelect = false,
  enableAction = false,
  showPagination = false,
  totalCount = 0,
  enableSimulation = false,
  getFilterDetails,
  parentPage,
}) {
  const ticketStatusNames = {
    101: "Draft",
    100: "New",
    150: "Confirm Legal Requirements",
    200: "Artifact Confirmation",
    300: "Evidence Submission",
    400: "Evidence Review",
    500: "Testing at TA reviewers",
    600: "Resolved",
    999: "Closed Incomplete",
  };
  const variantDetails = useSelector((state) => state.variantReducer);
  const ticketStatusMapping = {
    DRAFT: 101,
    NEW: 100,
    CONFIRM_LEGAL_REQUIREMENTS: 150,
    ARTIFACT_CONFIRMATION: 200,
    EVIDENCE_SUBMISSION: 300,
    EVIDENCE_REVIEW: 400,
    TESTING_AT_TS: 500,
    RESOLVED: 600,
    CANCELLED: 999,
  };

  const LinkButton = styled(Button)(({ theme }) => ({
    textTransform: "none",
    color: theme.palette.primary.main,
    textDecoration: "underline",
    padding: 0,
    minWidth: "auto",
    "&:hover": {
      textDecoration: "underline",
      backgroundColor: "transparent",
    },
  }));

  const CountdownTimer = styled(Typography)(({ theme }) => ({
    backgroundColor: "green",
    color: "white",
    borderRadius: 0,
    textAlign: "center",
    width: "100px",
    cursor: "pointer",
  }));
  // hooks
  const dispatch = useDispatch();

  const imagePathDetails = useSelector((state) => state?.profileReducer);
  const selectedProject = useSelector(
    (state) => state?.projectConfigReducer?.selectedProjectDetails,
  );
  const selectedVariant = useSelector(
    (state) => state.projectConfigReducer.selectedVariantDetails,
  );
  const profileDetails = useSelector((store) => store.profileReducer);
  const navigate = useNavigate();

  // states
  const [selectedProjectId, setSelectedProjectId] = useState(null);
  const [selectedVarientId, setSelectedVarientId] = useState(null);
  // const [ticketDetails, setTicketDetails] = useState([]);
  const [selectedComponent, setSelectedComponent] = useState(null);
  const [selectedComponentFromGetApi, setSelectedComponentFromGetApi] =
    useState();
  const [linkApiProps, setLinkApiProps] = useState(null);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [currentTicketId, setCurrentTicketId] = useState(null);
  const [open, setOpen] = useState(false);
  const [timer, setTimer] = useState(5);
  const [intervalId, setIntervalId] = useState(null);
  const [userDetailsMap, setUserDetailsMap] = useState(new Map());
  const [dialogData, setDialogData] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [eeDialogData, setEEDialogData] = useState(null);
  const [legalDialogData, setLegalDialogData] = useState(null);
  const [isLegalDialogOpen, setIsLegalDialogOpen] = useState(false);
  const [selectedTableRowData, setSelectedTableRowData] = useState(null);

  const [isViewTestArtifactsDialogOpen, setIsViewTestArtifactsDialogOpen] =
    useState(false);
  const [selectedTicketId, setSelectedTicketId] = useState(null);
  const [dynamicFormData, setDynamicFormData] = useState(null);
  const [isTemplateDialogOpen, setIsTemplateDialogOpen] = useState(false);
  const [isViewTraceTrackDialogOpen, setIsViewTraceTrackDialogOpen] =
    useState(false);
  const [isEEDialogOpen, setIsEEDialogOpen] = useState(false);
  const [tableRowsData, setTableRowsData] = useState([]);
  const [apiAction, setApiAction] = useState("");
  const [apiDataKey, setApiDataKey] = useState("");
  const [moduleViewMoreType, setModuleViewMoreType] = useState("");
  const [usersDetail, setUsersDetail] = useState(new Map());
  const [anchorEl, setAnchorEl] = useState(null);
  const fileInputRef = useRef(null);
  const [currentParams, setCurrentParams] = useState(null);
  const [openModal, setOpenModal] = React.useState(false);
  const [imagePath, setImagePath] = React.useState("");
  const [attachmentDialogOpen, setAttachmentDialogOpen] = useState(false);
  const [disableAction, setDisableAction] = useState(false);
  const [externalUrl, setExternalUrl] = useState(null);
  const [externalSource, setExternalSource] = useState(null);
  // Const
  const roleType = profileDetails?.profile?.role_details?.role_type;
  const supplier = roleType === 2;
  const ticketStatus = {
    100: (
      <Chip
        sx={{
          fontWeight: "bold",

          background: "none",
          color: "#000000",
          fontSize: "12px",
        }}
        label={"NEW"}
        size="medium"
      />
    ),
    101: (
      <Chip
        sx={{
          fontWeight: "bold",
          background: "none",
          color: "#0076a8",
          fontSize: "12px",
        }}
        label={"DRAFT"}
        size="medium"
      />
    ),
    150: (
      <Chip
        sx={{
          fontWeight: "bold",
          background: "none",
          color: "#0076a8",
          fontSize: "12px",
        }}
        label={"CONFIRM LEGAL REQUIREMENTS"}
        size="medium"
      />
    ),
    200: (
      <Chip
        sx={{
          fontWeight: "bold",
          background: "none",
          color: "#0076a8",
          fontSize: "12px",
        }}
        label={"ARTIFACT CONFIRMATION"}
        size="medium"
      />
    ),
    300: (
      <Chip
        sx={{
          fontWeight: "bold",
          background: "none",
          color: "#0076a8",
          fontSize: "12px",
        }}
        label={"EVIDENCE SUBMISSION"}
        size="medium"
      />
    ),
    400: (
      <Chip
        sx={{
          fontWeight: "bold",
          background: "none",
          color: "#0076a8",
          fontSize: "12px",
        }}
        label={"EVIDENCE REVIEW"}
        size="medium"
      />
    ),
    500: (
      <Chip
        sx={{
          fontWeight: "bold",
          background: "none",
          color: "#0076a8",
          fontSize: "12px",
        }}
        label={"TESTING AT TA reviewers"}
        size="medium"
      />
    ),
    600: (
      <Chip
        sx={{
          fontWeight: "bold",
          background: "none",
          color: "#26890D",
          fontSize: "12px",
        }}
        label={"RESOLVED"}
        size="medium"
      />
    ),
    999: (
      <Chip
        sx={{
          fontWeight: "bold",
          background: "none",
          color: "#26890D",
          fontSize: "12px",
        }}
        label={"CANCELLED"}
        size="medium"
      />
    ),
  };

  const getTabBasedOnTicketType = () => {
    switch (Number(tickets_type)) {
      case 6: // Vehicle System
        return 0;
      case 3: // Vehicle Component
        return 1;
      case 2: // EE
        return 2;
      case 1: // SWM
        return 3;
      default:
        return 0;
    }
  };

  const getComponentTypeByTicketType = (ticketType = tickets_type) => {
    switch (Number(ticketType)) {
      case 1:
        return "swm";
      case 2:
        return "ee";
      case 3:
        return "vc";
      case 6:
        return "vs";
      default:
        return "ee";
    }
  };

  const getSelectedComponentApiByTicketType = (rowData) => {
    const ticketType = Number(rowData?.tickets_type ?? tickets_type);
    const projectId = rowData?.project_details?.project_id;
    const variantId = rowData?.variant_details?.[0]?.variant_id;

    switch (ticketType) {
      case 2: {
        const eeParams = {
          project_id: Number(projectId),
          variant_id: Number(variantId),
          page_number: 1,
          page_size: 100,
        };
        if (rowData?.ee_details?.ee_id != null) {
          eeParams.ee_id = Number(rowData?.ee_details?.ee_id);
        }
        return {
          apiAction: labCar.get_ee_components,
          params: eeParams,
          matchFn: (item) =>
            Number(item?.ee_id) === Number(rowData?.ee_details?.ee_id),
        };
      }
      case 1: {
        const swmParams = {
          project_id: Number(projectId),
          variant_id: Number(variantId),
          page_number: 1,
          page_size: 100,
        };
        if (rowData?.ee_details?.ee_id != null) {
          swmParams.ee_id = Number(rowData?.ee_details?.ee_id);
        }
        if (rowData?.swm_details?.swm_id != null) {
          swmParams.swm_id = Number(rowData?.swm_details?.swm_id);
        }
        return {
          apiAction: labCar.get_sw_modules,
          params: swmParams,
          matchFn: (item) =>
            Number(item?.swm_id) === Number(rowData?.swm_details?.swm_id),
        };
      }
      case 6: {
        const vsParams = {
          project_id: Number(projectId),
          variant_id: Number(variantId),
          page_number: 1,
          page_size: 100,
        };
        if (rowData?.vehicle_system_details?.id != null) {
          vsParams.vs_id = Number(rowData?.vehicle_system_details?.id);
        }
        return {
          apiAction: labCar.get_vehicle_systems,
          params: vsParams,
          matchFn: (item) =>
            Number(item?.id ?? item?.vs_id) ===
            Number(rowData?.vehicle_system_details?.id),
        };
      }
      case 3: {
        const vcParams = {
          project_id: Number(projectId),
          variant_id: Number(variantId),
          page_number: 1,
          page_size: 100,
        };
        if (rowData?.vehicle_system_details?.id != null) {
          vcParams.vs_id = Number(rowData?.vehicle_system_details?.id);
        }
        if (rowData?.vehicle_component_details?.id != null) {
          vcParams.vc_id = Number(rowData?.vehicle_component_details?.id);
        }
        return {
          apiAction: labCar.get_vehicle_components,
          params: vcParams,
          matchFn: (item) =>
            Number(item?.id ?? item?.vc_id) ===
            Number(rowData?.vehicle_component_details?.id),
        };
      }
      default:
        return null;
    }
  };

  const handleClickOpen = (ticket_id, url = null, source) => {
    console.log("---handleClickOpen---", source);
    switch (source) {
      case "SNOW":
        setExternalSource("ServiceNow");
        break;
      case "JIRA":
        setExternalSource("Jira");
        break;
      default:
        setExternalSource("ServiceNow");
    }

    setCurrentTicketId(ticket_id);
    setExternalUrl(url);
    setOpen(true);
    setTimer(5);
  };

  const handleClose = () => {
    setOpen(false);
    if (intervalId) {
      clearInterval(intervalId);
    }
  };

  const handleMoreClick = (e, item) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
    setSelectedTableRowData(item); // Store the selected row data
  };
  const handleCloseMenu = () => {
    setAnchorEl(null);
    setSelectedTableRowData(null); // Clear the selected row data
  };

  const handleSimulationClick = (event, params) => {
    event.preventDefault();
    setCurrentParams(params);
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
    handleCloseMenu();
  };

  const handleFileSelect = (event) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      console.log("Selected files:", files);
      // Handle the selected files here
      handleImageChange(event, currentParams);
    }
  };

  const handleImageRowClick = ({ row }) => {
    console.log("View simulation file:", row);
    setOpenModal(true);
    // Handle viewing the simulation file
    dispatch(
      HandleApiActions({
        ...profileActions.get_profile_pic,
        show_toast: true,
        file_name: row?.simulation_file,
        is_file: true,
      }),
    );
  };

  const handleRedirect = () => {
    setOpen(false);
    if (intervalId) {
      clearInterval(intervalId);
      setIntervalId(null);
    }
    if (currentTicketId) {
      const linkType = currentTicketId.startsWith("INC")
        ? "incident.do"
        : "sn_customerservice_case.do";
      let url =
        externalUrl ||
        `https://ven07074.service-now.com/${linkType}?sysparm_query=number=${currentTicketId}`;
      window.open(url, "_blank");
    }
  };

  // const getTicketStatusName = (statusCode) => {
  //   return ticketStatus[statusCode] || "-";
  // };

  const handleImageChange = async (event, params) => {
    console.log("parrrwfwsfs", params);
    const file = event.target.files[0];
    setDisableAction(true);
    // Check if a file is selected
    if (file) {
      // Get the file extension
      const fileExtension = file.name.split(".").pop().toLowerCase();
      const allowedExtensions = ["jpg", "jpeg", "png"];
      if (allowedExtensions.includes(fileExtension)) {
        // dispatch(startLoading());
        // Upload the Profile Picture => Get the file name
        const resp = await dispatch(
          HandleApiActions({
            ...profileActions.upload_profile_pic,
            show_toast: true,
            file: file,
            is_file: true,
            ignore_app_loading_spinner: true,
          }),
        );

        await dispatch(
          HandleApiActions({
            api_name: "/ACM/intersections/upload/simulation-file",
            https_method: "PUT",
            params: {
              intr_id: params?.row?.intr_id,
              file_name: resp?.file_name,
            },
            show_toast: true,
            ignore_app_loading_spinner: true,
          }),
        );

        await fetchTickets();
        setDisableAction(false);
        // dispatch(stopLoading());
      } else {
        toast.dark("Please select a valid image file", {
          type: "error",
          autoClose: 3000,
        });
      }
    }
  };

  useEffect(() => {
    setImagePath(imagePathDetails?.profile_pic_path);
  }, [imagePathDetails]);
  /*************  ✨ Windsurf Command ⭐  *************/
  /**
   * Toggles the dialog open/closed state
   * @param {boolean} newOpen - True to open the dialog, false to close it
   */
  /*******  d8721e2c-0bfc-46ab-8b2b-5145cbf9e9eb  *******/
  const toggleDialog = (newOpen) => () => {
    // setIsDialogOpen(newOpen);
    setIsEEDialogOpen(newOpen);
  };

  const handleSWMViewButtonClick = (item) => {
    const modules = item?.dependencies?.software_modules || [];
    const vehicleFunctions = item?.swm_details?.vehicle_functions;
    setSelectedComponent(item);
    setApiAction(softwareIntersectionAPI.get_software_intersections);
    setLinkApiProps({
      swm_id: item?.swm_details?.swm_id,
      ee_id: item?.ee_details?.ee_id,
    });
    setApiDataKey("linked_swm_details");
    setModuleViewMoreType("SWM");

    // Map the modules to include both module details and their corresponding ee_details
    const items2 = modules?.map((module) => ({
      swm_id: module?.swm_id,
      name: module?.name,
      version: module?.version,
      description: module?.description,
      ee_details: module?.ee_details || [], // Include ee_details in each module
    }));

    const eeDetails = modules.flatMap((module) => module?.ee_details) || [];

    setDialogData({
      heading1: `Vehicle Functions`,
      items1:
        vehicleFunctions?.map((vehicleFunction) => ({
          name: vehicleFunction?.name,
          abbreviation: vehicleFunction?.abbreviation,
        })) || [],
      heading2: `Dependent Software Modules`,
      items2: items2, // Use the combined items
      heading3: `EE Details`,
      items3: eeDetails?.map((ee) => ({
        ee_id: ee?.ee_id,
        assembly_number: ee?.assembly_number,
        description: ee?.description,
        supplier_name: ee?.supplier_name,
        version: ee?.version,
      })),
    });

    toggleDialog(true)();
  };

  const handleEEViewButtonClick = (item) => {
    const modules = item?.dependencies?.ee_components || [];
    const vehicleSystems = item?.ee_details?.vehicle_systems;

    setSelectedComponent(item);
    setApiAction(labCar.get_ee_intersections);
    setLinkApiProps({
      ee_id: item?.ee_details?.ee_id,
    });
    setApiDataKey("linked_ee_details");
    setModuleViewMoreType("EE");
    setEEDialogData({
      heading1: `Vehicle Systems`,
      items1:
        vehicleSystems?.map((vs) => ({
          name: vs?.name,
          abbreviation: vs?.abbreviation,
        })) || [],
      heading2: `Dependent EE Components`,
      items2: modules?.map((ee) => ({
        ee_id: ee?.ee_id,
        assembly_number: ee?.assembly_number,
        description: ee?.description,
        supplier_name: ee?.supplier_name,
        version: ee?.version,
      })),
    });

    toggleEEDialog(true)();
  };

  const handleVSViewButtonClick = (item) => {
    console.log("---handleVSViewButtonClick---", item);

    setSelectedComponent(item);
    setApiAction(labCar.get_vehicle_systems);
    setLinkApiProps({
      vs_id: item?.vehicle_system_details?.id,
    });
    setSelectedComponent(item);
    setApiDataKey("linked_vs_details");
    setModuleViewMoreType("VS");

    toggleEEDialog(true)();
  };

  const handleVCViewButtonClick = (item) => {
    console.log("---handleVCViewButtonClick---", item);

    setSelectedComponent(item);
    setApiAction(labCar.get_vehicle_components);
    setLinkApiProps({
      vc_id: item?.vehicle_component_details?.id,
      vs_id: item?.vehicle_system_details?.id,
    });
    setSelectedComponent(item);
    setApiDataKey("linked_vc_details");
    setModuleViewMoreType("VC");

    toggleEEDialog(true)();
  };

  const toggleLegalDialog = (newOpen) => () => {
    setIsLegalDialogOpen(newOpen);
  };
  const handleLEGALViewButtonClick = (item) => {
    const lr_details = item?.lr_details || {};
    const ls_details = item?.ls_details || {};

    // Directly map lr_details to items2
    const items2 = [
      {
        lr_id: lr_details.lr_id,
        chapter: lr_details.chapter,
        description: lr_details.description,
        req_number: lr_details.req_number,
      },
    ];

    // Create items3 from ls_details
    const items3 = [
      {
        ls_id: ls_details.ls_id,
        standard_number: ls_details.standard_number,
        name: ls_details.name,
        revision: ls_details.revision,
        description: ls_details.description,
        chapters: ls_details.chapters,
      },
    ];

    setLegalDialogData({
      heading2: `Legal Requirement`,
      items2: items2,
      items3: items3, // Optionally include ls_details if needed
    });

    toggleLegalDialog(true)();
  };

  const DescriptionCell = ({ value }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const maxLength = 200;
    // Function to toggle the description view
    const handleToggle = () => {
      setIsExpanded(!isExpanded);
    };
    const displayText =
      isExpanded || !value || value.length <= maxLength
        ? value
        : `${value.substring(0, maxLength)}...`;

    return (
      <Box sx={{ textAlign: "justify" }}>
        <Typography variant="body2">
          {displayText || "No Description for the selected project"}
          {value && value.length > maxLength && (
            <Button onClick={handleToggle} sx={{ textTransform: "none", p: 0 }}>
              {isExpanded ? "Show Less" : "Show More"}
            </Button>
          )}
        </Typography>
      </Box>
    );
  };

  const toggleEEDialog = (newOpen) => () => {
    setIsEEDialogOpen(newOpen);
  };

  const fetchUserDetails = async () => {
    try {
      const apiDetails = userManagementApis?.get_users_list_pa;
      delete apiDetails.action_type;
      const resp = await dispatch(
        HandleApiActions({
          ...apiDetails,
          params: {
            page_size: 10000,
            page_number: 1,
          },
          show_toast: false,
        }),
      );

      if (!resp.error) {
        const users = resp?.data || [];
        const userMap = new Map(users?.map((user) => [user?.user_id, user]));
        setUserDetailsMap(userMap);
        setUsersDetail(users);
      }
    } catch (error) {
      console.error("Failed to fetch user details:", error);
    }
  };

  useEffect(() => {
    if (!open) return; // Prevent interval from creating when dialog is closed.

    setTimer(5); // Reset timer here so it’s set BEFORE interval starts.

    const id = setInterval(() => {
      setTimer((prev) => {
        if (prev <= 1) {
          clearInterval(id);
          handleRedirect();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      clearInterval(id);
    };
  }, [open]);

  useEffect(() => {
    setSelectedTicketType(tickets_type);
    fetchTickets();
  }, []);

  // column

  // sonar handler function
  const handleIntersectionChange = (checked, intrId) => {
    setSelectedIntersectionIds((prev) => {
      if (checked) {
        return [...prev, intrId];
      } else {
        return prev.filter((id) => id !== intrId);
      }
    });
  };
  const COLUMNS = [
    ...(enableSelect
      ? [
        {
          field: "select",
          headerName: "",
          headerAlign: "center",
          minWidth: 30,
          flex: 1,
          sortable: false,
          disableColumnMenu: true,
          renderCell: (params) => (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                pl: 2,
                width: "100%",
              }}
            >
              {params.row.status === ticketStatusMapping.DRAFT ? (
                <CustomCheckBoxV2
                  value={selectedIntersectionIds.includes(
                    params?.row?.intr_id,
                  )}
                  // onChange={(e) => {
                  //   setSelectedIntersectionIds((prev) => {
                  //     if (e.target.checked) {
                  //       return [...prev, params?.row?.intr_id];
                  //     } else {
                  //       return prev.filter(
                  //         (id) => id !== params?.row?.intr_id,
                  //       );
                  //     }
                  //   });
                  // } }
                  onChange={(e) =>
                    handleIntersectionChange(
                      e.target.checked,
                      params?.row?.intr_id,
                    )
                  }
                />
              ) : (
                <></>
              )}
            </Box>
          ),
        },
      ]
      : []),
    {
      field: "ticket_id",
      headerName: "Ticket Details",
      headerAlign: "center",
      minWidth: 180,
      flex: 1,
      renderCell: (params) => {
        const ee_details = params?.row?.ee_details || {};
        console.log("---Ticket Details---", params?.row);
        const renderDetails = (label, details) => (
          <Box
            sx={{
              marginBottom: "8px",
              p: 0,
              height: "100%",
            }}
          >
            <Box sx={{ fontWeight: "bold", mb: 1 }}>{label}</Box>
            {Object.entries(details).map(([key, value]) => (
              <Box
                key={key}
                sx={{
                  ...componentStyle.font.tableText,
                  display: "flex",
                  gap: 0.5,
                }}
              >
                {key.replace(/_/g, " ")}:{" "}
                <CustomToolTip
                  length={12}
                  style={{ ...componentStyle.font.tableText }}
                >
                  {value || "-"}
                </CustomToolTip>
              </Box>
            ))}
          </Box>
        );

        return (
          <Box
            sx={{
              textAlign: "left",
              ...componentStyle.font.tableText,

              p: 0,
              m: "-8px 0",
              width: "100%",
            }}
          >
            {renderDetails("", {
              "Ticket Id": params?.value ? (
                <LinkButton
                  sx={{ ...componentStyle.font.tableText, mt: -0.5 }}
                  onClick={() =>
                    handleClickOpen(
                      params.value,
                      params.row.external_url || null,
                      params.row.source_type || "SNOW",
                    )
                  }
                >
                  {params?.value}
                </LinkButton>
              ) : (
                "-"
              ),
              "Intersection Id": params?.row?.intr_id || "-",
              Status: ticketStatusNames[params?.row?.status],
              "Created By":
                userDetailsMap?.get(params?.row?.created_by)?.fullname || "-",
              "Updated By": params?.row?.updated_user_details?.[0]?.name,
              Description: params?.row?.description,
              Reason: params?.row?.reason,
            })}

            <CustomDialogBoxV2
              open={open}
              onClose={handleClose}
              title={<Box></Box>}
            >
              <Box
                sx={{
                  width: "400px",
                  p: 2,
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  flexDirection: "column",
                }}
              >
                <Typography
                  variant="h6"
                  sx={{ ...componentStyle.font.tabLevelHeader }}
                >
                  Redirecting to {externalSource} Ticketing tool!
                </Typography>

                <CountdownTimer
                  onClick={handleRedirect}
                  sx={{
                    width: "fit-content",
                    mt: 1,
                    px: 2,
                    ...componentStyle.font.tabLevelHeader,
                    fontWeight: 500,
                  }}
                >
                  Redirecting in ({timer}) sec..
                </CountdownTimer>
              </Box>
            </CustomDialogBoxV2>
          </Box>
        );
      },
    },
    {
      ...(Number(tickets_type) === TICKETS_TYPES.VEHICLE_SYSTEM ||
        Number(tickets_type) === TICKETS_TYPES.VEHICLE_COMPONENT
        ? {
          field: "vehicle_system_details",
          headerName: "Vehicle System",
          headerAlign: "left",
          width: 150,
          // flex: 3,
          renderCell: (params) => {
            const { vehicle_system_details } = params?.row;
            console.log("---vehicle_system_details---", params?.row);
            return (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                  width: "100%",
                }}
              >
                <Box sx={{ textAlign: "left", fontSize: "14px" }}>
                  <Box sx={{ ...componentStyle.font.tableText }}>
                    Name: {vehicle_system_details?.name || "-"}
                  </Box>
                  <Box sx={{ ...componentStyle.font.tableText }}>
                    Assembly Number:{" "}
                    {vehicle_system_details?.assembly_number || "-"}
                  </Box>
                  <Box sx={{ ...componentStyle.font.tableText }}>
                    Description: {vehicle_system_details?.description || "-"}
                  </Box>
                </Box>

                {!supplier && (
                  <Box sx={{ textAlign: "left", marginTop: 2 }}>
                    <CustomButtonV2
                      handleOnClick={(event) => {
                        handleVSViewButtonClick(params?.row?.allData);
                      }}
                      size="small"
                      btnName={"View More..."}
                    />
                  </Box>
                )}
              </Box>
            );
          },
        }
        : {
          field: "tickets_type",
          headerName: "Module Type",
          headerAlign: "left",
          minWidth: 200,
          flex: 1,
          renderCell: (params) => {
            const { swm_details, ee_details } = params?.row;

            const vehicle_component_details = [
              params?.row?.vehicle_component_details,
            ];

            const renderDetails = (label, details) => (
              <Box
                sx={{ height: "90px", minHeight: "90px", overflowY: "auto" }}
              >
                <Box
                  sx={{
                    marginBottom: "8px",
                    ...componentStyle.font.tableText,
                  }}
                >
                  {label}
                </Box>
                {Object.entries(details)?.map(([key, value]) => (
                  <Box key={key} sx={{ ...componentStyle.font.tableText }}>
                    {key.replace(/_/g, " ")}: {value || "-"}
                  </Box>
                ))}
              </Box>
            );

            return (
              <Box
                sx={{
                  textAlign: "left",
                  ...componentStyle.font.tableText,
                }}
              >
                {swm_details &&
                  Object.keys(swm_details).length > 0 &&
                  Number(params?.row?.ticket_type) === 1 ? (
                  <>
                    {renderDetails("Software Module", {
                      Number: swm_details?.name,
                      Version: swm_details?.version,
                      Description: swm_details?.description,
                    })}
                    {swm_details?.name && !supplier && (
                      <Box sx={{ textAlign: "left", marginTop: 2 }}>
                        <CustomButtonV2
                          handleOnClick={() => {
                            handleSWMViewButtonClick(params?.row?.allData);
                          }}
                          size="small"
                          btnName={"View More..."}
                        />
                      </Box>
                    )}
                  </>
                ) : ee_details &&
                  Object.keys(ee_details).length > 0 &&
                  Number(params?.row?.ticket_type) === 2 ? (
                  <>
                    {renderDetails("EE Components", {
                      "Supplier Name": ee_details?.supplier_name,
                      "Assembly Number": ee_details?.assembly_number,
                      Version: ee_details?.version,
                      Description: ee_details?.description,
                    })}
                    {params?.row?.allData?.dependencies?.ee_components &&
                      !supplier && (
                        <Box sx={{ textAlign: "left", marginTop: 2 }}>
                          <CustomButtonV2
                            handleOnClick={(event) => {
                              handleEEViewButtonClick(params?.row?.allData);
                            }}
                            size="small"
                            btnName={"View More..."}
                          />
                        </Box>
                      )}
                  </>
                ) : vehicle_component_details &&
                  vehicle_component_details.length > 0 ? (
                  <>
                    <Select
                      value={vehicle_component_details}
                      displayEmpty
                      sx={{ height: "40px", fontSize: "15px" }}
                      renderValue={(selected) => {
                        const selectedComponent =
                          vehicle_component_details.find(
                            (component) => component.id === selected,
                          );
                        return selectedComponent
                          ? `${selectedComponent.component} - ${selectedComponent.assembly_number}`
                          : "Vehicle Component";
                      }}
                    >
                      {vehicle_component_details &&
                        vehicle_component_details?.map((component) => (
                          <MenuItem
                            sx={{
                              fontSize: "15px",
                              display: "block",
                              padding: "10px 15px",
                            }}
                            key={`vehicle-${component.id}`}
                            value={component.id}
                          >
                            <Box sx={{ marginBottom: "4px" }}>
                              <strong>Component:</strong>{" "}
                              {component.component}
                            </Box>
                            <Box sx={{ marginBottom: "4px" }}>
                              <strong>Assembly No:</strong>{" "}
                              {component.assembly_number}
                            </Box>
                            <Box>
                              <strong>Summary:</strong> {component.summary}
                            </Box>
                          </MenuItem>
                        ))}
                    </Select>
                  </>
                ) : (
                  <Box sx={{ color: "gray" }}>No Data Available</Box>
                )}
              </Box>
            );
          },
        }),
    },
    ...(Number(tickets_type) === TICKETS_TYPES.VEHICLE_COMPONENT
      ? [
        {
          field: "vehicle_component_details",
          headerName: "Vehicle Component",
          headerAlign: "left",
          width: 150,
          // flex: 3,
          renderCell: (params) => {
            const { vehicle_component_details } = params?.row;
            return (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <Box
                  sx={{ textAlign: "left", ...componentStyle.font.tableText }}
                >
                  <Box>Name: {vehicle_component_details?.name || "-"}</Box>
                  <Box>
                    Component:{" "}
                    {vehicle_component_details?.component_number || "-"}
                  </Box>
                  <Box>
                    Description:{" "}
                    {vehicle_component_details?.description || "-"}
                  </Box>
                </Box>
                {!supplier && (
                  <Box sx={{ textAlign: "left", marginTop: 4 }}>
                    <CustomButtonV2
                      handleOnClick={(event) => {
                        handleVCViewButtonClick(params?.row?.allData);
                      }}
                      size="small"
                      btnName={"View More..."}
                    />
                  </Box>
                )}
              </Box>
            );
          },
        },
      ]
      : []),

    {
      field: "ls_lr_details",
      headerName: "Legal Details",
      headerAlign: "left",
      minWidth: 200,
      flex: 1,

      renderCell: (params) => {
        const { lr_details, ls_details } = params?.row;
        return (
          <Box sx={{ textAlign: "left", ...componentStyle.font.tableText }}>
            <Box sx={{ height: "90px", minHeight: "90px", overflowY: "auto" }}>
              <Box>Chapter: {lr_details?.chapter || "-"}</Box>
              <Box>Requirement Number: {lr_details?.req_number || "-"}</Box>
              <Box>Standard Number: {ls_details?.standard_number || "-"}</Box>
              <Box>Revision: {ls_details?.revision || "-"}</Box>
            </Box>

            <Box sx={{ textAlign: "left", marginTop: 2 }}>
              <CustomButtonV2
                handleOnClick={() => {
                  handleLEGALViewButtonClick(params?.row?.allData);
                }}
                size="small"
                btnName={"View More..."}
              />
            </Box>
          </Box>
        );
      },
    },
    {
      field: "test_case_details",
      headerName: "Test Case Details",
      headerAlign: "left",
      width: 180,
      renderCell: (params) => {
        const testCaseDetails = params?.row?.test_case_details ?? [];
        const hasTestCases = testCaseDetails.length > 0;

        return (
          <Box sx={{ width: "100%", borderRadius: "0px", paddingTop: "40px" }}>
            <Box sx={{ width: "100%" }}>
              <FormControl
                sx={{
                  ...componentStyle.form.select.formControl,
                  width: "160px",
                }}
              >
                <Select
                  size="small"
                  value=""
                  displayEmpty
                  fullWidth
                  renderValue={() =>
                    hasTestCases ? "View Test Cases" : "No Test Cases"
                  }
                  sx={{ ...componentStyle.form.select.select }}
                  MenuProps={{
                    PaperProps: {
                      style: {
                        width: "160px",
                      },
                      sx: { maxHeight: 300 },
                    },
                  }}
                >
                  {testCaseDetails.map((testCase) => (
                    <MenuItem
                      key={testCase.manual_test_case_id}
                      value={testCase.manual_test_case_id}
                      sx={{ ...componentStyle.form.select.menuItem }}
                    >
                      <Box
                        sx={{
                          p: 0,
                          m: 0,
                          width: "100%",
                          overflow: "hidden",
                        }}
                      >
                        <Tooltip
                          title={testCase?.manual_test_case_id}
                          placement="top"
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              ...componentStyle.font.tableText,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            <Box component="span" fontWeight="bold">
                              ID:
                            </Box>{" "}
                            {testCase?.manual_test_case_id ?? "-"}
                          </Typography>
                        </Tooltip>

                        <Tooltip title={testCase?.name} placement="top">
                          <Typography
                            variant="body2"
                            sx={{
                              ...componentStyle.font.tableText,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            <Box component="span" fontWeight="bold">
                              Name:
                            </Box>{" "}
                            {testCase?.name ?? "-"}
                          </Typography>
                        </Tooltip>

                        <Tooltip title={testCase?.description} placement="top">
                          <Typography
                            variant="body2"
                            sx={{
                              ...componentStyle.font.tableText,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              maxWidth: "100%", // ensures ellipsis
                            }}
                          >
                            <Box component="span" fontWeight="bold">
                              Description:
                            </Box>{" "}
                            {testCase?.description ?? "-"}
                          </Typography>
                        </Tooltip>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Box>
        );
      },
    },

    {
      field: "users",
      headerName: "Assignee",
      headerAlign: "center",
      width: 180,
      renderCell: (params) => {
        const techServiceUsers = params?.row?.tech_service_users || [];

        const oemUsers = params?.row?.oem_users || [];
        const supplierUsers = params?.row?.suppliers || [];
        const createdBy = params?.row?.created_by || "";
        const defaultUserDetails = {
          fullname: "User removed",
          email: "",
          role_details: {},
        };

        const allUserIds = [...techServiceUsers, ...oemUsers, ...supplierUsers];

        const techServiceUserOptions = techServiceUsers?.map((userId) => ({
          id: userId,
          ...(userDetailsMap?.get(userId) || defaultUserDetails),
        }));

        const oemUserOptions = oemUsers?.map((userId) => ({
          id: userId,
          ...(userDetailsMap?.get(userId) || defaultUserDetails),
        }));

        const supplierUserOptions = supplierUsers?.map((userId) => ({
          id: userId,
          ...(userDetailsMap?.get(userId) || defaultUserDetails),
        }));

        const techServiceDefault =
          techServiceUserOptions?.length > 0
            ? techServiceUserOptions[0]?.id
            : "";
        const oemDefault =
          oemUserOptions?.length > 0 ? oemUserOptions[0]?.id : "";

        const supplierDefault =
          supplierUserOptions?.length > 0 ? supplierUserOptions[0]?.id : "";

        return (
          <>
            <FormControl
              // fullWidth
              sx={{ ...componentStyle.form.select.formControl, width: "160px" }}
            >
              {techServiceUserOptions?.length > 0 && (
                <>
                  <label style={{ ...componentStyle.form.select.label }}>
                    Select Compliance Reviewers
                  </label>
                  <Select
                    value={techServiceDefault}
                    displayEmpty
                    sx={{ ...componentStyle.form.select.select }}
                    MenuProps={{
                      PaperProps: {
                        style: {
                          width: "160px",
                        },
                        sx: { maxHeight: 300 },
                      },
                    }}
                    renderValue={(selected) => {
                      const selectedUser = techServiceUserOptions?.find(
                        (user) => user?.id === selected,
                      );
                      return selectedUser
                        ? selectedUser?.fullname
                        : "No Compliance reviewers";
                    }}
                  >
                    {techServiceUserOptions?.map((user) => (
                      <MenuItem
                        sx={{
                          ...componentStyle.form.select.menuItem,
                          "&:hover": {
                            backgroundColor: "#e0e0e0 !important",
                          },

                          // Selected
                          "&.Mui-selected": {
                            backgroundColor: "#ebebeb !important",
                          },

                          // Selected + Hover
                          "&.Mui-selected:hover": {
                            backgroundColor: "#e0e0e0 !important",
                          },
                        }}
                        key={`tech-${user?.id}`}
                        value={user?.id}
                      >
                        <Box
                          sx={{
                            p: 0,
                            m: 0,
                            width: "100%",
                            overflow: "hidden",
                          }}
                        >
                          {user?.fullname}
                          <br />
                          {user?.email}
                          <br />
                          {user?.role_details?.role_name}
                          <br />
                          {user?.role_details?.department}
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </>
              )}

              {oemUserOptions?.length > 0 && (
                <>
                  <label
                    style={{
                      ...componentStyle.form.select.label,
                      marginTop:
                        techServiceUserOptions?.length > 0 ? "10px" : "0",
                    }}
                  >
                    Select Compliance Requestors
                  </label>
                  <Select
                    value={oemDefault}
                    MenuProps={{
                      PaperProps: {
                        style: {
                          width: "160px",
                        },
                        sx: { maxHeight: 300 },
                      },
                    }}
                    displayEmpty
                    sx={{
                      ...componentStyle.form.select.select,
                    }}
                    renderValue={(selected) => {
                      const selectedUser = oemUserOptions?.find(
                        (user) => user?.id === selected,
                      );
                      return selectedUser
                        ? selectedUser?.fullname
                        : "No TA requestors";
                    }}
                  >
                    {oemUserOptions?.map((user) => (
                      <MenuItem
                        sx={{
                          ...componentStyle.form.select.menuItem,
                          "&:hover": {
                            backgroundColor: "#e0e0e0 !important",
                          },

                          // Selected
                          "&.Mui-selected": {
                            backgroundColor: "#ebebeb !important",
                          },

                          // Selected + Hover
                          "&.Mui-selected:hover": {
                            backgroundColor: "#e0e0e0 !important",
                          },
                        }}
                        key={`oem-${user?.id}`}
                        value={user?.id}
                      >
                        {user?.fullname}
                        <br />
                        {user?.email}
                        <br />
                        {user?.role_details?.role_name}
                        <br />
                        {user?.role_details?.department}
                      </MenuItem>
                    ))}
                  </Select>
                </>
              )}

              {supplierUserOptions?.length > 0 && (
                <>
                  <label
                    style={{
                      ...componentStyle.form.select.label,
                      marginTop: supplierUserOptions?.length > 0 ? "10px" : "0",
                    }}
                  >
                    Suppliers
                  </label>
                  <Select
                    value={supplierDefault}
                    displayEmpty
                    sx={{
                      ...componentStyle.form.select.select,
                    }}
                    renderValue={(selected) => {
                      const selectedUser = supplierUserOptions?.find(
                        (user) => user?.id === selected,
                      );
                      return selectedUser
                        ? selectedUser?.fullname
                        : "No Suppliers";
                    }}
                    MenuProps={{
                      PaperProps: {
                        style: {
                          width: "160px",
                        },
                        sx: { maxHeight: 300 },
                      },
                    }}
                  >
                    {supplierUserOptions?.map((user) => (
                      <MenuItem
                        sx={{
                          ...componentStyle.form.select.menuItem,
                          "&:hover": {
                            backgroundColor: "#e0e0e0 !important",
                          },

                          // Selected
                          "&.Mui-selected": {
                            backgroundColor: "#ebebeb !important",
                          },

                          // Selected + Hover
                          "&.Mui-selected:hover": {
                            backgroundColor: "#e0e0e0 !important",
                          },
                        }}
                        key={`sup-${user?.id}`}
                        value={user?.id}
                      >
                        {user?.fullname}
                        <br />
                        {user?.email}
                        <br />
                        {user?.role_details?.role_name}
                        <br />
                        {user?.role_details?.department}
                      </MenuItem>
                    ))}
                  </Select>
                </>
              )}

              {techServiceUserOptions?.length === 0 &&
                oemUserOptions?.length === 0 &&
                supplierUserOptions?.length === 0 && (
                  <Typography sx={componentStyle.font.tableText}>
                    No Users Available
                  </Typography>
                )}
            </FormControl>
          </>
        );
      },
    },

    {
      field: "updated_at",
      headerName: "Changed At",
      headerAlign: "center",
      width: 100,
      renderCell: (params) => (
        <Box sx={{ textAlign: "center", ...componentStyle.font.tableText }}>
          {params?.value || "-"}
        </Box>
      ),
    },
    {
      field: "created_at",
      headerName: "Created At",
      headerAlign: "center",
      width: 100,
      renderCell: (params) => (
        <Box sx={{ textAlign: "center", ...componentStyle.font.tableText }}>
          {params?.value || "-"}
        </Box>
      ),
    },
    ...(enableAction
      ? [
        {
          field: "action",
          headerName: "Action",
          headerAlign: "center",
          minWidth: 30,
          flex: 1,
          sortable: false,
          disableColumnMenu: true,
          renderCell: (params) => (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                width: "100%",
              }}
            >
              <Tooltip title="More">
                <IconButton
                  size="small"
                  sx={{ color: "black" }}
                  disabled={disableAction}
                  onClick={(e) => handleMoreClick(e, params?.row)}
                >
                  <MoreVertIcon fontSize="small" />
                </IconButton>
              </Tooltip>

              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleCloseMenu}
                PaperProps={{
                  sx: {
                    borderRadius: 0, // ✅ remove rounding completely
                    boxShadow: "none", // optional: remove shadow if you want a flat look
                  },
                }}
              >
                <MenuItem
                  onClick={() => {
                    handelOpenAttachment(params);
                  }}
                  sx={{ ...componentStyle.font.text }}
                >
                  View Attachment
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    navigate(
                      `/dashboard/v2/type-approval/traceTrack?project_id=${selectedProject.project_id
                      }&variant_id=${selectedVariant.variant_id
                      }&parent_page=${parentPage}&intr_id=${selectedTableRowData?.intr_id
                      }&tab=${getTabBasedOnTicketType()}`,
                    );
                  }}
                  // disabled={item?.status === true ? false : true}
                  sx={{ ...componentStyle.font.text }}
                >
                  View Track
                </MenuItem>

                {enableSimulation &&
                  (!selectedTableRowData?.simulation_file ||
                    selectedTableRowData?.simulation_file.trim() === "" ? (
                    <MenuItem
                      onClick={(event) => {
                        handleSimulationClick(event, {
                          row: selectedTableRowData,
                        });
                        setAnchorEl(null);
                      }}
                      disabled={
                        selectedTableRowData?.status !== 101 ? true : false
                      }
                      sx={{ ...componentStyle.font.text }}
                    >
                      Upload Simulation File
                    </MenuItem>
                  ) : (
                    <MenuItem
                      onClick={() => {
                        handleImageRowClick({ row: selectedTableRowData });
                        setAnchorEl(null);
                      }}
                      sx={{ ...componentStyle.font.text }}
                    >
                      View Simulation Files
                    </MenuItem>
                  ))}
              </Menu>
            </Box>
          ),
        },
      ]
      : []),
  ];

  useEffect(() => {
    if (ticketDetails) {
      const filteredTicketList = ticketDetails
        // ?.sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at))
        // ?.filter((item) => item?.status !== 101)
        ?.map((item, index) => ({
          ...item,
          id: (page - 1) * rowsPerPage + index + 1,
          intr_id: item?.intr_id,
          external_url: item?.external_url,
          source_type: item?.source_type,
          tech_service_users: item?.tech_service_users,
          oem_users: item?.oem_users,
          users: item?.tech_service_users || item?.oem_users,
          ticket_id: item?.ticket_id,
          tickets_type: item?.tickets_type,
          status: item?.status,
          ticket_type: item?.ticket_type,
          ticket_status: item?.ticket_id || item?.status,
          ls_details: item?.ls_details,
          lr_details: item?.lr_details,
          swm_details: item?.swm_details,
          vehicle_component_details: item?.vehicle_component_details,
          ee_details: item?.ee_details,
          description: item?.description,
          reason: item?.reason,
          created_at: ConvertUTCToDate(
            item?.created_at,
            profileDetails?.profile?.profile?.region,
          ),
          created_by: item?.created_by,
          updated_at: ConvertUTCToDate(
            item?.updated_at,
            profileDetails?.profile?.profile?.region,
          ),
          updated_by: item?.updated_by,
          allData: item,
        }));

      setTableRowsData(filteredTicketList);
    }
  }, [ticketDetails]);

  useEffect(() => {
    fetchUserDetails();
  }, []);

  //function to open attachemnt dialog
  const handelOpenAttachment = async (data) => {
    console.log(data, "an attachment data");
    console.log(selectedTableRowData, "selectedTableRowData");
    const projectId = selectedTableRowData?.project_details?.project_id;
    const varientId = selectedTableRowData?.variant_details?.[0]?.variant_id;
    const intr_id = selectedTableRowData?.intr_id;
    const selectedComponentApi =
      getSelectedComponentApiByTicketType(selectedTableRowData);
    const apiDetails = typeApprovalAPI.get_user_dynamic_forms;
    setSelectedComponentFromGetApi(null);

    const [formResp, componentResp] = await Promise.all([
      dispatch(
        HandleApiActions({
          ...apiDetails,
          show_toast: false,
          params: {
            project_id: Number(projectId),
            variant_id: Number(varientId),
            intr_id: intr_id,
            page_number: 1,
            page_size: 100,
            is_ticket_required: true,
          },
        }),
      ),
      selectedComponentApi?.apiAction
        ? dispatch(
          HandleApiActions({
            ...selectedComponentApi.apiAction,
            show_toast: false,
            params: selectedComponentApi.params,
          }),
        )
        : Promise.resolve({ error: true }),
    ]);

    if (formResp.error) {
      setDynamicFormData(null);
    } else {
      setDynamicFormData(formResp?.data?.[0]);
    }

    if (componentResp?.error) {
      setSelectedComponentFromGetApi(null);
    } else {
      const componentList = Array.isArray(componentResp?.data)
        ? componentResp.data
        : [];
      const matchedComponent = selectedComponentApi?.matchFn
        ? componentList.find(selectedComponentApi.matchFn) || componentList?.[0]
        : componentList?.[0] || null;
      setSelectedComponentFromGetApi(matchedComponent);
    }

    setAttachmentDialogOpen(true);
    setAnchorEl(null);
  };

  console.log(dynamicFormData, "dynamicFormData,dynamicFormData");
  return (
    <Box sx={{ height: "450px", width: "100%" }}>
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileSelect}
        style={{ display: "none" }}
        multiple
      />
      <CustomDataGridV2
        rows={tableRowsData}
        columns={COLUMNS}
        rowCount={tableRowsData?.length}
        isMultiLine={true}
        showPagination={showPagination}
        enableMore={showPagination ? false : hasTicketNeedMore}
        page={page}
        rowsPerPage={rowsPerPage}
        handlePageChange={(page) => {
          setPage(page);
          fetchTickets({ page_number: page });
        }}
        totalItems={totalCount}
        handleMore={() => {
          let ticketType = tickets_type;
          navigate(
            `/dashboard/type-approval?project_id=${selectedProject.project_id}&linkModuleType=${ticketType}`,
          );
        }}
      />
      {console.log(
        "---selectedComponent ModuleViewMoreDialog---",
        selectedComponent,
      )}
      <ModuleViewMoreDialog
        isEEDialogOpen={isEEDialogOpen}
        toggleEEDialog={toggleEEDialog}
        selectedComponent={selectedComponent}
        linkApiProps={linkApiProps}
        apiAction={apiAction}
        apiDataKey={apiDataKey}
        type={moduleViewMoreType}
      />

      {/* Image */}
      <CustomDialogBoxV2
        open={openModal}
        onClose={() => setOpenModal(false)}
        title={"View Image"}
      >
        <Box sx={{ width: "400px", height: "400px" }}>
          <img
            alt="No Image Found"
            src={imagePath}
            style={{
              height: "100%",
              width: "100%",
              objectFit: "contain", // Adjust object-fit as needed
            }}
          />
        </Box>
      </CustomDialogBoxV2>
      {/* Legal Dialoug */}
      <CustomDialogBoxV2
        open={isLegalDialogOpen}
        onClose={toggleLegalDialog(false)}
        title={
          <Box
            sx={{
              fontWeight: "bold",
              color: "black",
              fontSize: "15px",
              display: "flex",
              width: "100%",
              justifyContent: "center",
              flex: 1,
            }}
          >
            Legal Details
          </Box>
        }
      >
        <Box sx={{ width: "650px" }}>
          {/* Legal Standards */}
          <Box>
            {legalDialogData?.items3 && (
              <Box>
                {legalDialogData?.items3?.map((ls, index) => (
                  <Box
                    key={index}
                    sx={{
                      // border: "1px solid black",
                      padding: "10px",
                      borderRadius: "10px",
                    }}
                  >
                    <Typography
                      sx={{
                        fontWeight: "bold",
                        color: "black",
                        fontSize: "15px",
                      }}
                    >
                      Legal Standard
                    </Typography>
                    <Box sx={{ gap: "10px" }}>
                      {[
                        { header: "ID", value: ls?.ls_id },
                        {
                          header: "Standard Number",
                          value: ls?.standard_number,
                        },
                        { header: "Name", value: ls?.name },
                        { header: "Revision", value: ls?.revision },
                        { header: "Description", value: ls?.description },
                        {
                          header: "Chapters",
                          value: ls?.chapters.join(", "),
                        }, // Display chapters
                      ]?.map((det, key) => (
                        <Typography key={key} sx={{ fontSize: "14px" }}>
                          {det?.header}: {det?.value}
                        </Typography>
                      ))}
                    </Box>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
          {/* Legal Requirements */}
          <Box>
            {legalDialogData?.heading2 && (
              <Box>
                {legalDialogData?.items2?.map((lr, index) => (
                  <Box
                    key={index}
                    sx={{
                      // border: "1px solid black",
                      padding: "10px",
                      borderRadius: "10px",
                    }}
                  >
                    <Typography
                      sx={{
                        fontWeight: "bold",
                        color: "black",
                        fontSize: "15px",
                      }}
                    >
                      Legal Requirements
                    </Typography>
                    <Box sx={{ gap: "10px" }}>
                      {[
                        { header: "ID", value: lr?.lr_id },
                        { header: "Chapter", value: lr?.chapter },
                        {
                          header: "Requirement Number",
                          value: lr?.req_number,
                        },
                        {
                          header: "Description",
                          value: lr?.description,
                        },
                      ]?.map((det, key) => (
                        <Typography key={key} sx={{ fontSize: "14px" }}>
                          {det?.header}: {det?.value}
                        </Typography>
                      ))}
                    </Box>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        </Box>
      </CustomDialogBoxV2>

      {/* testcase artifact dialog */}
      <CustomDialogBoxV2
        open={attachmentDialogOpen}
        onClose={() => setAttachmentDialogOpen(false)}
        title="Attachment"
      // contentSx={{padding:0}}
      >
        <Box sx={{ width: "800px", height: "500px", p: 0 }}>
          <TicketAttachment
            dynamicFormData={dynamicFormData}
            onClose={() => setAttachmentDialogOpen(false)}
            selectedTableRowData={selectedTableRowData}
            setSelectedTableRowData={setSelectedTableRowData}
            setDynamicFormData={setDynamicFormData}
            fetchTickets={fetchTickets}
            displayAddArtifact={parentPage != "simulations"}
            selectedComponent={selectedComponentFromGetApi}
            componentType={getComponentTypeByTicketType()}
          />
        </Box>
      </CustomDialogBoxV2>
    </Box>
  );
}

export default TicketsTable;
